export type StatelessType = "concentrated" | "distributed";

export interface StatelessGroup {
  id: string;
  name: string;
  color: string;       // muted palette — less cinematic
  population: number;  // thousands
  description: string;
  since: string;
  cause: string;
  type: StatelessType;
  relatedGroups?: string[]; // ids
  countries: string[];
}

export const statelessGroups: StatelessGroup[] = [
  {
    id: "rohingya",
    name: "Rohingya",
    color: "#b04040",
    population: 3500,
    description: "Predominantly Muslim ethnic group from Myanmar's Rakhine State. Denied citizenship since 1982, facing genocidal violence and fleeing to Bangladesh, Malaysia, Indonesia, and beyond.",
    since: "1982",
    cause: "Burma Citizenship Law (1982) excluded Rohingya from 135 recognized ethnic groups.",
    type: "concentrated",
    relatedGroups: ["stateless_myanmar"],
    countries: ["MM","BD","MY","ID","TH","IN","SA","PK"],
  },
  {
    id: "palestinians",
    name: "Palestinian Stateless",
    color: "#4060a0",
    population: 5900,
    description: "Palestinians lacking full citizenship, spread across refugee camps in Jordan, Lebanon, Syria, and the occupied territories — the world's largest refugee population.",
    since: "1948",
    cause: "Displacement following the 1948 Arab-Israeli war (Nakba) and ongoing military occupation.",
    type: "distributed",
    countries: ["PS","JO","LB","SY","EG","SA","KW","AE"],
  },
  {
    id: "kurds",
    name: "Kurds (Stateless Nation)",
    color: "#a07030",
    population: 40000,
    description: "Largest stateless ethnic group in the world, spread across Turkey, Iran, Iraq, and Syria without their own recognized state. Cultural and political suppression across all four countries.",
    since: "1923",
    cause: "Post-WWI peace settlements (Treaty of Lausanne, 1923) denied Kurds a promised homeland.",
    type: "distributed",
    countries: ["TR","IR","IQ","SY","DE","SE","NL"],
  },
  {
    id: "bedoon",
    name: "Bedoon",
    color: "#2d7a5a",
    population: 100,
    description: "Stateless Arabs in the Gulf states — primarily Kuwait and the UAE — who failed to register during censuses in the 1960s, now denied citizenship despite generational residence.",
    since: "1960s",
    cause: "Failed to register during Gulf state nationality censuses in the 1960s; excluded from citizenship.",
    type: "concentrated",
    countries: ["KW","AE","SA","QA","BH"],
  },
  {
    id: "stateless_baltics",
    name: "Non-Citizens (Baltics)",
    color: "#604090",
    population: 280,
    description: "Primarily ethnic Russians in Latvia and Estonia who were not granted citizenship after independence from the USSR, living with 'non-citizen' travel documents for over 30 years.",
    since: "1991",
    cause: "Baltic independence (1991): automatic citizenship granted only to pre-1940 residents and descendants.",
    type: "concentrated",
    countries: ["LV","EE"],
  },
  {
    id: "dom",
    name: "Dom People",
    color: "#1a6a80",
    population: 1000,
    description: "An itinerant people of South Asian origin living across the Middle East and North Africa, related to the Romani. Often unregistered, stateless, and facing severe marginalization.",
    since: "Historical",
    cause: "South Asian diaspora across MENA for centuries; no recognized homeland, rarely registered at birth.",
    type: "distributed",
    countries: ["SY","IQ","EG","JO","LB","YE","IR"],
  },
  {
    id: "bihari",
    name: "Stranded Pakistanis (Bihari)",
    color: "#506020",
    population: 300,
    description: "Urdu-speaking Muslims who supported Pakistan in the 1971 Bangladesh Liberation War. Stranded in Bangladesh after partition, denied full citizenship for over 50 years.",
    since: "1971",
    cause: "Supported Pakistan in the 1971 war; Bangladesh viewed them as Pakistanis, Pakistan refused to repatriate.",
    type: "concentrated",
    countries: ["BD"],
  },
  {
    id: "haitian_dominican",
    name: "Haitian-Dominicans",
    color: "#806020",
    population: 200,
    description: "Dominicans of Haitian descent stripped of citizenship by a retroactive 2013 court ruling, rendering tens of thousands stateless in the country of their birth.",
    since: "2013",
    cause: "Dominican Constitutional Court ruling TC-168-13 (2013) stripped citizenship from those born to undocumented parents retroactively.",
    type: "concentrated",
    countries: ["DO","HT"],
  },
  {
    id: "faili_kurds",
    name: "Faili Kurds",
    color: "#1a6050",
    population: 300,
    description: "Shia Kurds stripped of Iraqi citizenship under Saddam Hussein during the Iran-Iraq War, expelled to Iran and left stateless between two hostile states.",
    since: "1980",
    cause: "Denationalized by Saddam Hussein as a security threat at the start of the Iran-Iraq War (1980).",
    type: "concentrated",
    relatedGroups: ["kurds"],
    countries: ["IQ","IR"],
  },
  {
    id: "kenya_nubians",
    name: "Kenyan Nubians",
    color: "#5040a0",
    population: 100,
    description: "Descendants of Sudanese soldiers brought to Kenya under British colonial rule in the early 20th century. Denied citizenship for generations despite being born and raised in Kenya.",
    since: "Early 1900s",
    cause: "Colonial legacy: Nubians were brought as soldiers but never integrated into any postcolonial citizenship framework.",
    type: "concentrated",
    countries: ["KE"],
  },
  {
    id: "lhotshampa",
    name: "Lhotshampa",
    color: "#904030",
    population: 110,
    description: "Nepali-speaking Bhutanese expelled in the early 1990s. Most spent decades in UNHCR camps in Nepal; over 100,000 have since been resettled in third countries.",
    since: "1990",
    cause: "Bhutan's 1990 nationality law and 'One Nation, One People' policy led to mass expulsion of Nepali-speakers.",
    type: "concentrated",
    countries: ["NP","BT","IN","US","CA","AU"],
  },
  {
    id: "stateless_myanmar",
    name: "Other Myanmar Minorities",
    color: "#7a3060",
    population: 800,
    description: "Various ethnic minorities in Myanmar — Karen, Chin, Kachin, Shan and others — denied full citizenship under the 1982 law and facing forced displacement and violence.",
    since: "1982",
    cause: "Myanmar 1982 Citizenship Law restricted full citizenship to 135 officially recognized ethnic groups.",
    type: "concentrated",
    relatedGroups: ["rohingya"],
    countries: ["MM","TH","IN","CN"],
  },
];
